import { type EditAction } from '@/application';
import { type EntitySource } from '@/domain/construction';
import { createEntity, type EntityId, isPlaneSource, toneForPlane } from '@/domain/model';
import { NO_ROTATION, ProjectionKind } from '@/domain/solid';
import { hitTest } from '@/engine';

import { currentSnap, inPlaneAt } from './pointFactory';
import {
  type PointerInput,
  type Tool,
  type ToolContext,
  type ToolPrompt,
  type ToolResponse,
} from './Tool';

/**
 * Planes with no solid to cut — the blackboard figure.
 *
 * ## The report
 *
 * *"giáo viên đứng lớp chỉ có mỗi cái bảng đen… không lẽ phải vẽ 1 khối ra trước?"* — and until
 * now the answer was yes. Every plane construction needed a body, so the figure of the whole
 * chapter on quan hệ song song and quan hệ vuông góc — two leaning parallelograms and the line
 * where they cross — could not be built at all.
 *
 * ## One click, and the second one leans
 *
 * The first click brings the figure's viewpoint and a level plane; the second brings a plane
 * leaning off it, so **two clicks give the figure the chapter is about**. The tilts are chosen so
 * that the pair reads immediately: level and thirty is what a hand draws when it wants two planes
 * to be seen as two.
 *
 * Neither number is final — `PlanePanel` sets the angle, and that is where "hợp với (P) một góc
 * 60°" is said exactly. The tool's job is to put something true on the page in one click.
 *
 * ## Why the viewpoint is made here and not asked for
 *
 * A viewpoint is not a thing on the page and no teacher would think to create one; it exists
 * because a drawing of space has to be *drawn from somewhere*. The same reasoning that made a
 * solid create its own hidden anchor applies word for word, and `unreachableParentsOf` already
 * sweeps both away when the last thing resting on them is deleted.
 */
export class FreePlaneTool implements Tool {
  readonly id = 'plane-free';
  readonly label = 'Mặt phẳng rời';
  readonly shortcut = 'd';
  readonly group = 'Dựng hình';

  prompt(): ToolPrompt | null {
    return {
      tool: this.label,
      step: 0,
      steps: 1,
      hint: 'nhấp vào chỗ trống — mặt phẳng đầu nằm ngang, mặt phẳng sau nghiêng lên',
    };
  }

  onPointerDown(context: ToolContext, input: PointerInput): ToolResponse | null {
    if (input.button !== 0) return null;

    const actions: EditAction[] = [];
    const existing = [...context.document.entities.entries()].find(
      ([, entity]) => entity.source.op === 'space'
    );

    /*
     * One viewpoint for the figure, shared. Two planes drawn through different projections make a
     * picture that is geometrically false and looks perfectly normal — so a second plane joins the
     * first rather than bringing another.
     */
    let space = existing?.[0];
    if (space === undefined) {
      const snapped = currentSnap(context, input);
      const anchor = context.nextId('c');
      actions.push({
        kind: 'add-entity',
        entity: createEntity(anchor, { op: 'free-point', position: snapped.world }, context.style, {
          visible: false,
          isConstruction: true,
        }),
      });

      space = context.nextId('sp');
      actions.push({
        kind: 'add-entity',
        entity: createEntity(
          space,
          {
            op: 'space',
            center: anchor,
            rotation: NO_ROTATION,
            projection: ProjectionKind.Isometric,
          },
          context.style,
          { visible: false, isConstruction: true }
        ),
      });
    }

    const planes = [...context.document.entities.values()].filter((entity) =>
      isPlaneSource(entity.source)
    ).length;

    const id = context.nextId('pf');
    const source: EntitySource = {
      op: 'plane-free',
      space,
      tilt: planes === 0 ? 0 : LEANING,
      size: DEFAULT_SIZE,
    };
    // Toned so the second one can be told from the first where they cross — see `toneForPlane`.
    actions.push({
      kind: 'add-entity',
      entity: createEntity(id, source, { ...context.style, ...toneForPlane(planes) }),
    });

    return { actions, selection: [id], preview: [] };
  }

  onPointerMove(): ToolResponse | null {
    return { cursor: 'copy' };
  }

  cancel(): ToolResponse | null {
    return null;
  }

  reset(): void {
    /* stateless: one click places a plane */
  }
}

/** Half the drawn width. Wide enough to write `(P)` on, small enough for two to fit on a page. */
const DEFAULT_SIZE = 200;

/**
 * How far the second plane leans.
 *
 * Not sixty, which is the commonest angle a problem *states*, because this is the angle a figure
 * is *drawn* at before anybody says otherwise. Thirty reads as two distinct surfaces while leaving
 * both washes wide enough to write a name on; sixty puts the second one nearly edge-on and the
 * figure looks like a plane and a line.
 */
const LEANING = 30;

/**
 * A point placed on a plane by clicking it.
 *
 * The one gesture that makes a blackboard figure more than two washes: a line **in** a plane is a
 * segment between two of these, an axis to turn about is two of them, and "mặt phẳng qua M song
 * song với (P)" needs one. Everything the editor already does with points then works.
 *
 * `(u, v)` are read off the plane's own drawn axes, so the click lands where the pointer is and
 * the point stays there when the plane tilts — see `inPlaneAt` in `SelectTool`, which is the same
 * arithmetic run for the drag.
 */
export class PlanePointTool implements Tool {
  readonly id = 'plane-point';
  readonly label = 'Điểm trên mặt phẳng';
  readonly shortcut = 'J';
  readonly group = 'Dựng hình';

  prompt(): ToolPrompt | null {
    return {
      tool: this.label,
      step: 0,
      steps: 1,
      hint: 'nhấp vào một mặt phẳng rời',
    };
  }

  onPointerDown(context: ToolContext, input: PointerInput): ToolResponse | null {
    if (input.button !== 0) return null;

    const found = this.planeAt(context, input);
    if (found === null) return null;

    const id = context.nextId('op');
    return {
      actions: [
        {
          kind: 'add-entity',
          entity: createEntity(
            id,
            { op: 'plane-point', plane: found.plane, u: found.u, v: found.v },
            context.style
          ),
        },
      ],
      selection: [id],
      preview: [],
    };
  }

  onPointerMove(context: ToolContext, input: PointerInput): ToolResponse | null {
    return { cursor: this.planeAt(context, input) === null ? 'not-allowed' : 'copy' };
  }

  /** The free plane under the pointer, and where the click lands in its own axes. */
  private planeAt(
    context: ToolContext,
    input: PointerInput
  ): { plane: EntityId; u: number; v: number } | null {
    const hit = hitTest(context.scene, input.world, context.tolerance, {
      filter: (id) => context.document.entities.get(id)?.source.op === 'plane-free',
    });
    if (hit === null) return null;

    const placed = inPlaneAt(context.scene.values.get(hit.id), input.world);
    return placed === null ? null : { plane: hit.id, ...placed };
  }

  cancel(): ToolResponse | null {
    return null;
  }

  reset(): void {
    /* stateless */
  }
}

export function createFreePlaneTools(): readonly Tool[] {
  return [new FreePlaneTool(), new PlanePointTool()];
}
